import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/setup/redux/RootReducer';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import * as jobTitle from '../redux/JobTitleRedux';
import { getJobTitles, createJobTitle } from '../redux/JobTitleCRUD';
import { LabeledField } from '@/__core/components/LabeledField';
import AddButtonSelect from '@/__core/hocs/AddButtonSelect';
import { MessageSnackbar } from '@/__core/helpers/components';
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';

interface JobTitleSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  labelWidth?: number;
}

export default function JobTitleSelect({
  value,
  onChange,
  label = "Chức vụ",
  placeholder = "Chọn chức vụ",
  disabled = false,
  required = false,
  labelWidth,
}: JobTitleSelectProps) {
  const dispatch = useDispatch();
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();
  const normalizedValue = typeof value === 'string'
    ? value
    : String((value as any)?.id || value || '');
  
  // State for notifications
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Redux selectors
  const jobTitles = useSelector((state: RootState) => state.jobTitles.jobTitles);
  const jobTitlesLoading = useSelector((state: RootState) => state.jobTitles.loading);

  // Handle adding new job title
  const handleAddNewJobTitle = async (newJobTitle: { name: string }) => {
    try {
      setErrorMessage(null);
      startGlobalLoading('Đang tạo chức vụ mới...', 'job-title-create');
      const response = await createJobTitle({
        name: newJobTitle.name,
        description: "",
        is_active: true,
      });
      
      // Refresh job titles list to get the updated list with the new job title
      const updatedResponse = await getJobTitles();
      const jobTitlesData = updatedResponse.data.results || updatedResponse.data || [];
      dispatch(jobTitle.actions.fulfillJobTitles(jobTitlesData));
      
      // Find the newly created job title from the updated list
      const newJobTitleFromList = jobTitlesData.find((jobTitle: any) => 
        jobTitle.name === newJobTitle.name
      );
      
      // Show success message
      setSuccessMessage(`Chức vụ "${newJobTitle.name}" đã được tạo thành công!`);
      
      if (newJobTitleFromList) {
        return {
          id: newJobTitleFromList.id.toString(),
          name: newJobTitleFromList.name,
        };
      } else {
        // Fallback: return the name as both id and name
        return {
          id: newJobTitle.name,
          name: newJobTitle.name,
        };
      }
    } catch (error: any) {
      console.error("Error creating job title:", error);
      setErrorMessage(formatErrorForSnackbar(error) || "Có lỗi xảy ra khi tạo chức vụ mới");
      throw error;
    } finally {
      stopGlobalLoading('job-title-create');
    }
  };

  return (
    <>
      <LabeledField label={label} required={required} labelWidth={labelWidth}>
        <AddButtonSelect
          options={jobTitles || []}
          value={normalizedValue}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          disabled={disabled || jobTitlesLoading}
          placeholder={jobTitlesLoading ? "Đang tải..." : placeholder}
          onAddNew={handleAddNewJobTitle}
          addDialogTitle="Thêm chức vụ mới"
          addDialogLabel="Tên chức vụ"
          addDialogPlaceholder="Nhập tên chức vụ"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3b82f6',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
          }}
        />
      </LabeledField>

      {/* Success Snackbar */}
      <MessageSnackbar
        open={!!successMessage}
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage(null)}
      />

      {/* Error Snackbar */}
      <MessageSnackbar
        open={!!errorMessage}
        message={errorMessage}
        severity="error"
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
}
